import {
  groupLinkSchema,
  inviteLinkCheckSchema,
  inviteLinkJoinSchema,
  okSchema,
  syncArchivePreviewSchema,
  syncFamiliesResponseSchema,
  syncFamilySchema,
  syncPeopleResponseSchema,
  syncPersonSchema,
  syncResultSchema,
  whatsAppSyncSchema,
  type GroupLink,
  type InviteLinkCheck,
  type InviteLinkJoin,
  type LinkSwimmersRequest,
  type SetSyncRolesRequest,
  type SortSyncPersonRequest,
  type SyncArchivePreview,
  type SyncFamiliesResponse,
  type SyncFamily,
  type SyncPeopleView,
  type SyncPerson,
  type SyncResult,
  type WhatsAppSync,
} from "@teamna/shared";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { z } from "zod";
import { api } from "../api/client.ts";
import { queryKeys } from "./keys.ts";

/**
 * The WhatsApp group sync tool (strategy repo: teamna/strategy/whatsapp-group-sync.md). One set of
 * hooks for the one sheet that every page opens: Tools, Members, Team and the setup import step.
 */

const base = (businessId: string) => `/business/businesses/${businessId}`;

/**
 * A sort, a sync or an archive changes families, the team, members and the checklist. `exceptLinks`
 * leaves the group-link polls alone: the poll calls this from inside its own fetch, and refreshing
 * itself would cancel that fetch and start it again, for ever, so the link would never read as
 * connected.
 */
function refreshAfterSync(queryClient: QueryClient, businessId: string, options: { exceptLinks?: boolean } = {}): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.setup.all(businessId),
    // Never the archive preview: right after an archive it would ask about a person who is not
    // "left" any more (a 409), while its dialog closes. It reads fresh each time the dialog opens.
    predicate: (query) => query.queryKey[3] !== "archive-preview" && !(options.exceptLinks && query.queryKey[2] === "group-link"),
  });
  void queryClient.invalidateQueries({ queryKey: queryKeys.owner.members(businessId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
}

/** GET .../whatsapp-sync: the mode, the number to add, the groups, the waiting connect, the counts. */
export function useWhatsAppSync(businessId: string): UseQueryResult<WhatsAppSync> {
  return useQuery({
    queryKey: queryKeys.setup.whatsAppSync(businessId),
    queryFn: () => api.get(`${base(businessId)}/whatsapp-sync`, whatsAppSyncSchema),
  });
}

/** GET .../whatsapp-sync/people?view=. */
export function useSyncPeople(businessId: string, view: SyncPeopleView): UseQueryResult<SyncPerson[]> {
  return useQuery({
    queryKey: queryKeys.setup.syncPeople(businessId, view),
    queryFn: async () => (await api.get(`${base(businessId)}/whatsapp-sync/people?view=${view}`, syncPeopleResponseSchema)).items,
  });
}

/** POST .../group-links from the sync tool: new people land in Unsorted. The number always stays in the group. */
export function useConnectGroup(businessId: string): UseMutationResult<GroupLink, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`${base(businessId)}/group-links`, { purpose: "members", sortLater: true }, groupLinkSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.setup.whatsAppSync(businessId) }),
  });
}

const findResultSchema = z.object({ link: groupLinkSchema, sync: syncResultSchema.nullable() });
export type FindGroupResult = z.infer<typeof findResultSchema>;

/**
 * POST .../group-links/:id/find: "Check again". Looks for the group among the groups the Teamna
 * number is in now (one read at the gateway). `sync` null and the link still waiting: not there
 * yet. The link connected with `sync` null: the webhook was first (internal review F1.3).
 */
export function useFindGroup(businessId: string): UseMutationResult<FindGroupResult, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId) => api.post(`${base(businessId)}/group-links/${linkId}/find`, {}, findResultSchema),
    onSuccess: (result) => {
      if (result.link.status !== "connected") return;
      // The answer carries the link as the server has it now: keep it, and refresh the rest.
      queryClient.setQueryData(queryKeys.setup.groupLink(businessId, result.link.id), result.link);
      refreshAfterSync(queryClient, businessId, { exceptLinks: true });
    },
  });
}

/** POST .../group-links/:id/invite/check: the group behind an invite link, read without joining. Changes nothing. */
export function useCheckInviteLink(businessId: string): UseMutationResult<InviteLinkCheck, unknown, { linkId: string; link: string }> {
  return useMutation({
    mutationFn: ({ linkId, link }) => api.post(`${base(businessId)}/group-links/${linkId}/invite/check`, { link }, inviteLinkCheckSchema),
  });
}

/**
 * POST .../group-links/:id/invite/join: the Teamna number joins with the invite link and the group
 * syncs. `approval_needed`: the link keeps waiting, and the poll sees it connect after the approval.
 */
export function useJoinInviteLink(businessId: string): UseMutationResult<InviteLinkJoin, unknown, { linkId: string; link: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, link }) => api.post(`${base(businessId)}/group-links/${linkId}/invite/join`, { link }, inviteLinkJoinSchema),
    onSuccess: (result) => {
      if (result.result === "synced") refreshAfterSync(queryClient, businessId);
    },
  });
}

/**
 * GET .../group-links/:id every 3 seconds while it waits. Our own API only: the Whapi webhook
 * connects the link, so polling costs nothing at the gateway. When it connects, the tool refreshes.
 */
export function useWaitingGroup(businessId: string, linkId: string | null): UseQueryResult<GroupLink> {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.setup.groupLink(businessId, linkId ?? "none"),
    queryFn: async () => {
      const key = queryKeys.setup.groupLink(businessId, linkId ?? "none");
      const before = queryClient.getQueryData<GroupLink>(key)?.status;
      const link = await api.get(`${base(businessId)}/group-links/${linkId}`, groupLinkSchema);
      if (link.status === "connected" && before !== "connected") refreshAfterSync(queryClient, businessId, { exceptLinks: true });
      return link;
    },
    enabled: Boolean(linkId),
    refetchInterval: (query) => (query.state.data?.status === "connected" ? false : 3_000),
  });
}

/** Staging only: POST .../group-links/:id/simulate syncs the demo group. */
export function useDemoGroup(businessId: string): UseMutationResult<SyncResult, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (linkId) =>
      (await api.post(`${base(businessId)}/group-links/${linkId}/simulate`, {}, z.object({ sync: syncResultSchema }).passthrough())).sync,
    onSuccess: () => refreshAfterSync(queryClient, businessId),
  });
}

/** POST .../group-links/:id/sync: "Sync now" on a group where the number stays. */
export function useSyncNow(businessId: string): UseMutationResult<SyncResult, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId) => api.post(`${base(businessId)}/group-links/${linkId}/sync`, {}, syncResultSchema),
    onSuccess: () => refreshAfterSync(queryClient, businessId),
  });
}

/** POST .../group-links/:id/leave: "Remove Teamna from this group". */
export function useLeaveGroup(businessId: string): UseMutationResult<unknown, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId) => api.post(`${base(businessId)}/group-links/${linkId}/leave`, {}, okSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.setup.whatsAppSync(businessId) }),
  });
}

/** POST .../whatsapp-sync/people/:id/sort: Skip, or parent, swimmer and coach. */
export function useSortSyncPerson(
  businessId: string,
): UseMutationResult<SyncPerson, unknown, { id: string; idempotencyKey: string } & Partial<SortSyncPersonRequest>> {
  const queryClient = useQueryClient();
  return useMutation({
    // One key for one sort: a second press with the same key replays the first answer.
    mutationFn: ({ id, idempotencyKey, ...body }) =>
      api.post(`${base(businessId)}/whatsapp-sync/people/${id}/sort`, body, syncPersonSchema, { idempotencyKey }),
    onSuccess: () => refreshAfterSync(queryClient, businessId),
  });
}

/** POST .../whatsapp-sync/people/:id/keep. */
export function useKeepSyncPerson(businessId: string): UseMutationResult<SyncPerson, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`${base(businessId)}/whatsapp-sync/people/${id}/keep`, {}, syncPersonSchema),
    onSuccess: () => refreshAfterSync(queryClient, businessId),
  });
}

/** GET .../whatsapp-sync/people/:id/archive-preview, read when the owner opens "Archive". */
export function useArchivePreview(businessId: string, id: string | null): UseQueryResult<SyncArchivePreview> {
  return useQuery({
    queryKey: [...queryKeys.setup.whatsAppSync(businessId), "archive-preview", id ?? "none"],
    queryFn: () => api.get(`${base(businessId)}/whatsapp-sync/people/${id}/archive-preview`, syncArchivePreviewSchema),
    enabled: Boolean(id),
    // A fresh read every time the dialog opens: a booking made since the last look must show.
    staleTime: 0,
    gcTime: 0,
  });
}

/** POST .../whatsapp-sync/people/:id/archive. */
export function useArchiveSyncPerson(businessId: string): UseMutationResult<SyncPerson, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`${base(businessId)}/whatsapp-sync/people/${id}/archive`, {}, syncPersonSchema),
    onSuccess: () => refreshAfterSync(queryClient, businessId),
  });
}

/** PUT .../whatsapp-sync/people/:id/roles: Change on a saved or skipped row. No role is Skip. */
export function useSetSyncRoles(businessId: string): UseMutationResult<SyncPerson, unknown, { id: string } & SetSyncRolesRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.put(`${base(businessId)}/whatsapp-sync/people/${id}/roles`, body, syncPersonSchema),
    onSuccess: () => refreshAfterSync(queryClient, businessId),
  });
}

/** GET .../whatsapp-sync/families: step 2, the parents still to link and every swimmer at the business. */
export function useSyncFamilies(businessId: string): UseQueryResult<SyncFamiliesResponse> {
  return useQuery({
    queryKey: queryKeys.setup.syncFamilies(businessId),
    queryFn: () => api.get(`${base(businessId)}/whatsapp-sync/families`, syncFamiliesResponseSchema),
  });
}

/** POST .../whatsapp-sync/families/:id/swimmers: Done or Later on a parent of step 2. */
export function useLinkSwimmers(
  businessId: string,
): UseMutationResult<SyncFamily, unknown, { id: string; idempotencyKey: string } & Partial<LinkSwimmersRequest>> {
  const queryClient = useQueryClient();
  return useMutation({
    // One key for one Done: a retry after a lost answer replays it, so no swimmer is added twice.
    mutationFn: ({ id, idempotencyKey, ...body }) =>
      api.post(`${base(businessId)}/whatsapp-sync/families/${id}/swimmers`, body, syncFamilySchema, { idempotencyKey }),
    // The Families page reads under the same "setup" key, so it refreshes too.
    onSuccess: () => refreshAfterSync(queryClient, businessId),
  });
}
